<template>
	<div>
		<v-data-table
			id="idListagemDocumentos"
			v-if="hasItens"
			v-model="documentosSelecionados"
			:headers="colunas"
			:items="documentos"
			:show-select="!isAtaCancelada && hasAuthority && isEditar && quantidadeDocumentosSelecionaveis > 0"
			item-key="id"
			hide-default-footer
			:server-items-length="totalItens"
			:options="paginacaoInterna"
			no-data-text="Nenhum documento por aqui"
			@click:row="visualizarDocumentoAnexo"
			class="arp-listview row-pointer"
		>
			<template #[`header.data-table-select`]="{}">
				<v-simple-checkbox
					id="caixaDeSelecao"
					:value="todosItensEstaoSelecionados"
					color="primary"
					@input="onSelectAll($event)"
					@click.stop
				></v-simple-checkbox>
			</template>

			<template #[`item.data-table-select`]="{ isSelected, select, item }">
				<v-simple-checkbox
					v-if="itemPodeSelecionar(item)"
					id="caixaDeSelecao"
					class="row-checkbox"
					color="primary"
					:value="isSelected"
					@input="select($event)"
					@click.stop
				></v-simple-checkbox>
			</template>

			<template #[`item.tipoRegistro`]="{ item }">
				<v-row justify="center">
					<v-tooltip top>
						<template #activator="{ on, attrs }">
							<v-icon v-bind="attrs" v-on="on" @click.prevent.stop
								>{{
									estaDesatualizado(item) || estaJuntadoDesatualizado(item)
										? 'mdi-paperclip-remove'
										: tipoDocumentoRegistro[item.tipoRegistroDocumento].icon
								}}
							</v-icon>
						</template>
						<span>{{
							estaDesatualizado(item) || estaJuntadoDesatualizado(item)
								? 'Desatualizado - Foi gerada uma nova versão do documento'
								: tipoDocumentoRegistro[item.tipoRegistroDocumento].text
						}}</span>
					</v-tooltip>
				</v-row>
			</template>

			<template #[`item.signatarios`]="{ item }">
				<v-menu
					v-if="ehDocumentoProcessoDigital(item)"
					id="iconeSignatariosConvidados"
					open-on-hover
					top
					left
					offset-x
					:close-on-content-click="false"
				>
					<template #activator="{ on, attrs }">
						<div v-bind="attrs" v-on="on" @click.stop>
							<v-icon size="18" class="mb-1 mr-4">mdi-account-outline</v-icon>
							<span>{{ getQuantidadeAssinadosDocumento(item) }}</span>
						</div>
					</template>

					<v-list v-if="!ehListaSignatariosVazia(item)" id="listaSignatarios">
						<v-list-item-group>
							<v-list-item v-for="signatario in getListaSignatarios(item)" :key="signatario.email">
								<v-list-item-icon>
									<v-icon size="18" :color="getCorIconeSituacaoSignatario(signatario.situacao)">
										{{ getIconeSituacaoSignatario(signatario.situacao) }}
									</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>
										<texto-tooltip :texto="signatario.email" :tamanho-maximo="30"></texto-tooltip>
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
							<v-list-item v-if="item.signatarios.itens.length > 5">
								<v-list-item-content>
									<v-list-item-title>
										<v-btn
											text
											block
											@click="tratarEventoVisualizarSignatarios(item.id)"
											color="primary"
											>Ver Todos
										</v-btn>
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list-item-group>
					</v-list>
				</v-menu>
			</template>

			<template #[`item.titulo`]="{ item }">
				<v-tooltip :disabled="!item.tipoDocumento" max-width="800" top color="black">
					<template #activator="{ on, attrs }">
						<span v-bind="attrs" v-on="on" :class="getClasseLinhasPorSituacao(item)">{{
							getTituloDocumento(item)
						}}</span>
						<span>{{ item.nomeTipoDocumento }}</span>
					</template>
					<span>{{ 'Tipo: ' + item.tipoDocumento }}</span>
				</v-tooltip>
			</template>

			<template #[`item.dataCadastro`]="{ item }">
				<texto-tooltip
					:class="getClasseLinhasPorSituacao(item)"
					:texto="formatDataFilter(item.dataCadastro)"
					:texto-tooltip="`Autor: ${item.autor}`"
				></texto-tooltip>
			</template>

			<template #item.situacao="{ item }">
				<span :class="getClasseLinhasPorSituacao(item)"> {{ estaJuntado(item) }}</span>
			</template>

			<template #[`item.acoes`]="{ item }">
				<div class="d-flex flex-row justify-space-between align-center align-content-center px-0">
					<div class="d-flex align-center ml-auto" v-if="isEditar">
						<v-tooltip v-if="exibirIconeSituacaoEDocs(item)" top>
							<template #activator="{ on }">
								<v-icon :color="colorsSituacaoEDocs[item.situacaoEdocs]" v-on="on">
									{{ iconesSituacaoEDocs[item.situacaoEdocs] }}
								</v-icon>
							</template>
							<span>{{ mensagensSituacaoEDocs[item.situacaoEdocs] }}</span>
						</v-tooltip>
						<botao-tooltip-datatable
							v-if="podeExibirAcaoAssinar(item)"
							v-az-auth="
								montarPermissoes(
									permissoes.pedidoUtilizacaoDocumentoProcessoDigitalAssinaturaEletronicaCriar,
									permissoes.pedidoUtilizacaoDocumentoProcessoDigitalAssinaturaDigitalCriar,
								)
							"
							icone="mdi-draw-pen"
							cor="grey"
							texto="Assinar"
							@click="abrirModalSelecionarTipoAssinatura(item)"
						></botao-tooltip-datatable>
						<botao-tooltip-datatable
							icone="mdi-file-edit"
							cor="grey"
							texto="Redigir"
							@click="redigirDocumento(item)"
							v-if="hasAuthority && podeExibirAcaoRedigir(item)"
							v-az-auth="permissoes.pedidoUtilizacaoDocumentoEditorTextoRedigir"
						></botao-tooltip-datatable>
						<botao-tooltip-datatable
							v-if="item.podeJuntar && isProcessoDigitalAtivo && !isAtaCancelada"
							v-az-auth="permissoes.pedidoUtilizacaoDocumentoProcessoDigitalJuntarEmLote"
							icone="mdi-file-replace"
							texto="Juntar"
							cor="grey"
							@click="abrirOuFecharModalJuntarDocumento(item)"
						></botao-tooltip-datatable>
					</div>
					<div>
						<acessar-pedido-documentos-itens-btn-acoes
							:documento-ou-anexo="item"
							@baixar="baixarDocumentoAnexo(item)"
							@excluir="abrirOuFecharModalExclusaoDocumentoAnexo(item)"
							@concluir-edicao-documento="abrirOuFecharModalConcluirEdicaoDocumento(item)"
							@tratar-evento-desentranhar="tratarEventoDesentranhar(item)"
							@cancelar="abrirModalInformarMotivoCancelamento(item)"
							@convidar-signatarios="tratarEventoConvidarSignatarios(item)"
							@enviar-fornecedor="tratarEventoEnviarFornecedor(item)"
						></acessar-pedido-documentos-itens-btn-acoes>
					</div>
				</div>
			</template>
		</v-data-table>

		<paginacao-tabela
			v-if="hasItens"
			:page.sync="paginacaoInterna.page"
			:rows-per-page.sync="paginacaoInterna.rowsPerPage"
			:totalPaginas="totalPaginas"
			:totalItens="totalItens"
			@resetar-page="resetarPage"
		></paginacao-tabela>

		<div v-if="isEditar">
			<modal-confirmacao
				id="excluir-documento-ou-anexo-listagem"
				:exibir="exibirModalExclusaoDocumentoAnexo"
				:mensagem="getMensagemModalConfirmacaoExclusao()"
				@fechar-modal="abrirOuFecharModalExclusaoDocumentoAnexo"
				@confirmar="excluirDocumentoAnexo"
				:subtituloModal="'Deseja realmente excluir'"
				:tituloModal="getTituloModalConfirmacaoExclusao()"
			></modal-confirmacao>

			<formulario-motivo-cancelamento
				id="cancelar-documento-juntado"
				v-if="exibirModalInformarMotivoCancelamento"
				:exibir="exibirModalInformarMotivoCancelamento"
				titulo="Informe o Motivo do Cancelamento"
				@fechar-modal="abrirModalInformarMotivoCancelamento"
				:titulo-documento="documentoSelecionado.titulo"
				:id-documento="documentoSelecionado.id"
				@confirmar="cancelarDocumento"
			></formulario-motivo-cancelamento>

			<modal-confirmacao
				id="juntar-documento-listagem"
				:tituloModal="getMensagemModalConfirmacaoJuntado()"
				:subtituloModal="mensagemModalJuntarDocumento"
				:exibir="exibirModalJuntarDocumento"
				:mensagem="getCorpoMensagemModalConfirmacaoJuntado()"
				:label-acao-confirmar="'CONFIRMAR'"
				:label-acao-cancelar="'CANCELAR'"
				@fechar-modal="cancelarAcaoJuntarDocumentoProcessoDigital"
				@confirmar="juntarDocumentoProcessoDigital"
			></modal-confirmacao>

			<modal-confirmacao
				id="concluir-edicao-documento"
				:tituloModal="getMensagemModalConfirmacaoConcluirEdicaoDocumento()"
				:exibir="exibirModalConcluirEdicaoDocumento"
				:mensagem="getCorpoMensagemModalConcluirEdicaoDocumento()"
				:label-acao-confirmar="'CONFIRMAR'"
				:label-acao-cancelar="'CANCELAR'"
				@fechar-modal="abrirOuFecharModalConcluirEdicaoDocumento"
				@confirmar="concluirEdicaoDocumento"
			></modal-confirmacao>
		</div>
	</div>
</template>
<script>
import tabelaMixin from '@/views/mixins/tabela'
import documentoMixin from '@/views/mixins/documento'
import {
	actionTypes,
	mutationTypes,
	opcaoSimNao,
	situacaoSignatarioDocumentoProcessoDigital,
	situacoes,
	situacoesEdocs,
} from '@/core/constants'
import PaginacaoTabela from '@/views/components/PaginacaoTabela.vue'
import { TabelaColunaUtils } from '@/core/utils'
import BotaoTooltipDatatable from '@/views/components/BotaoTooltipDatatable.vue'
import AcessarPedidoDocumentosItensBtnAcoes from '@/views/pages/pedido/acessar/documentos/botoes/AcessarDocumentosPedidoBtnMaisAcoes.vue'
import arpBpmMixin from '@/views/mixins/bpm/arp-bpm-mixin'
import TextoTooltip from '@/views/components/TextoTooltip.vue'
import { cloneDeep } from 'lodash'
import signatario from '@/views/mixins/signatarios/signatario'
import FormularioMotivoCancelamento from '@/views/components/documentos/motivo-cancelamento/FormularioMotivoCancelamento.vue'
import { mapState } from 'vuex'
import formatDataFilter from '@/views/filters/format-data-filter'
import TooltipInformativo from '@/views/components/TooltipInformativo.vue'

export default {
	name: 'acessar-pedido-documentos-listagem',
	components: {
		TooltipInformativo,
		TextoTooltip,
		AcessarPedidoDocumentosItensBtnAcoes,
		BotaoTooltipDatatable,
		PaginacaoTabela,
		FormularioMotivoCancelamento,
	},
	mixins: [tabelaMixin, documentoMixin, arpBpmMixin, signatario],
	inject: ['isEditar', 'idPedido'],
	props: {
		documentos: {
			type: Array,
			required: true,
		},
	},
	emits: ['visualizar-signatarios', 'convidar-signatarios', 'enviar-fornecedor'],
	data() {
		return {
			paginacaoInterna: this.paginacao,
			exibirModalExclusaoDocumentoAnexo: false,
			exibirModalJuntarDocumento: false,
			documentoSelecionado: null,
			mensagemModalJuntarDocumento: null,
			exibirModalInformarMotivoCancelamento: false,
			isProcessoDigitalAtivo: this.$store.state.parametrosSistema.isProcessoDigitalAtivo,
			isIntegracaoEdocsAtivo: this.$store.state.parametrosSistema.isIntegracaoEdocsAtivo,
			exibirModalConcluirEdicaoDocumento: false,
			colorsSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'orange',
				[situacoesEdocs.SINCRONIZADO.value]: 'green',
			},
			iconesSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'mdi-alert-circle',
				[situacoesEdocs.SINCRONIZADO.value]: 'mdi-check-circle',
			},
			mensagensSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'Aguardando processamento para envio ao E-docs',
				[situacoesEdocs.SINCRONIZADO.value]: 'Enviado ao E-docs',
			},
		}
	},
	computed: {
		...mapState({
			parametrosSistema: (state) => state.parametrosSistema,
			infoPedido: (state) => state.pedido.informacoesPedido,
		}),
		documentosSelecionados: {
			get() {
				return this.$store.state.pedido.documentoPedido.documentosSelecionados
			},
			set(documentos) {
				this.$store.commit(mutationTypes.PEDIDO.DOCUMENTOS.CHECKBOX.DOCUMENTOS_SELECIONADOS, documentos)
				this.$store.commit(mutationTypes.COMUM.MODAL_ASSINATURA_FLOWBEE.SET_DOCUMENTOS, documentos)
			},
		},
		isSelecionouTodosItensDaListaDeDocumentosPedido: {
			get() {
				return this.$store.state.pedido.documentoPedido.isSelecionouTodosItensDaListaDeDocumentosPedido
			},
		},
		colunas() {
			let colunas = [
				new TabelaColunaUtils('', 'tipoRegistro', '2%'),
				new TabelaColunaUtils('Título', 'titulo', '40%'),
				new TabelaColunaUtils('Data cadastro', 'dataCadastro', '15%'),
			]

			if (this.isProcessoDigitalAtivo) {
				colunas.push(new TabelaColunaUtils('Convidados', 'signatarios', '15%'))
				colunas.push(new TabelaColunaUtils('Juntado', 'situacao', '10%'))
			}

			colunas.push(new TabelaColunaUtils('Ações', 'acoes', '12%'))

			return colunas
		},
		todosItensEstaoSelecionados() {
			return this.quantidadeDocumentosSelecionaveis === this.quantidadeDocumentosSelecionadosPaginaAtual
		},
		quantidadeDocumentosSelecionadosPaginaAtual() {
			return this.documentosSelecionados.filter((documentoSelecionado) => {
				if (this.documentos.findIndex((documento) => documento.id === documentoSelecionado.id) !== -1) {
					return documentoSelecionado
				}
			}).length
		},
		quantidadeDocumentosSelecionaveis() {
			return this.documentos.filter((documento) => this.itemPodeSelecionar(documento)).length
		},
		isAtaCancelada() {
			return this.infoPedido.ataRegistroPreco?.situacao === situacoes.ATA.CANCELADO.value
		},
	},
	async created() {
		await this.carregarInstancia(`${this.idPedido}`)
	},
	methods: {
		formatDataFilter,
		getClasseLinhasPorSituacao(documentoOuAnexo) {
			if (this.estaCancelado(documentoOuAnexo)) return 'text-decoration-line-through'
			if (this.estaDesatualizado(documentoOuAnexo)) return 'documento-desatualizado'
			if (this.estaJuntadoDesatualizado(documentoOuAnexo)) return 'documento-desatualizado'
			return ''
		},
		estaJuntadoDesatualizado(documentoOuAnexo) {
			return this.documentoOuAnexoEstaJuntadoDesatualizado(documentoOuAnexo)
		},
		tratarEventoVisualizarSignatarios(idDocumento) {
			this.$emit('visualizar-signatarios', idDocumento)
		},
		tratarEventoEnviarFornecedor(documento) {
			this.$emit('enviar-fornecedor', documento.id)
		},
		tratarEventoConvidarSignatarios(documento) {
			this.$emit('convidar-signatarios', { idAssociacao: documento.id, titulo: documento.titulo })
		},
		ehDocumentoProcessoDigital(item) {
			return item.situacao && item.situacao !== situacoes.DOCUMENTO.CRIADO.value && this.isProcessoDigitalAtivo
		},
		getOrdemSituacao(situacao) {
			if (situacao === situacaoSignatarioDocumentoProcessoDigital.ASSINADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.ASSINADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.EXPIRADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.EXPIRADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.ENVIADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.ENVIADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.EXCLUIDO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.EXCLUIDO.ordem
			}
		},
		getListaSignatarios(documento) {
			let documentoCopia = cloneDeep(documento)
			return documentoCopia.signatarios.itens
				.sort((item1, item2) => {
					if (this.getOrdemSituacao(item1.situacao) > this.getOrdemSituacao(item2.situacao)) return -1

					if (this.getOrdemSituacao(item1.situacao) < this.getOrdemSituacao(item2.situacao)) return 1

					return 0
				})
				.slice(0, 5)
		},
		getQuantidadeAssinadosDocumento(documento) {
			if (documento.signatarios) {
				return `${documento.signatarios.quantidadeSignatariosAssinaram} / ${documento.signatarios.quantidadeSignatariosConvidados}`
			}
		},
		ehListaSignatariosVazia(documento) {
			return !documento.signatarios || documento.signatarios.itens.length === 0
		},
		podeExibirAcaoAssinar(item) {
			return (
				(this.isAnexoPdf(item) || this.isDocumento(item)) &&
				this.documentoOuAnexoEstaEmRascunho(item) &&
				item.jaAssinou === false &&
				this.isProcessoDigitalAtivo &&
				!this.isAtaCancelada
			)
		},
		abrirModalSelecionarTipoAssinatura(documento) {
			this.$store.commit(mutationTypes.COMUM.MODAL_ASSINATURA_FLOWBEE.SET_DOCUMENTOS, [documento])
			this.$store.commit(mutationTypes.COMUM.MODAL_ASSINATURA_FLOWBEE.SET_EXIBICAO, true)
		},
		tratarEventoDesentranhar(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			if (this.possuiTermoDesentranhamento(documento)) {
				this.$emit('continuar-elaboracao-termo-desentranhamento', documento)
				return
			}
			this.$emit('criar-termo-desentranhamento', documento)
		},
		possuiTermoDesentranhamento(documento) {
			return opcaoSimNao.SIM.value === documento.possuiTermoDesentranhamento
		},
		async visualizarDocumentoAnexo(documentoOuAnexo) {
			// if (!this.hasAuthority) return
			if (
				this.documentoOuAnexoEstaDesatualizado(documentoOuAnexo) ||
				this.documentoOuAnexoEstaJuntadoDesatualizado(documentoOuAnexo)
			)
				return
			if (this.isDocumento(documentoOuAnexo) && this.documentoEstaCriado(documentoOuAnexo)) {
				const editor = await this.$store.dispatch(
					actionTypes.PEDIDO.DOCUMENTOS.DOCUMENTO.EDITOR_TEXTO.VISUALIZAR,
					documentoOuAnexo.id,
				)
				this.redirecionarPara(editor.urlVisualizar)

				return
			}

			await this.baixarDocumentoAnexo(documentoOuAnexo)
		},
		async redigirDocumento(documento) {
			if (this.isDocumento(documento)) {
				const editor = await this.$store.dispatch(
					actionTypes.PEDIDO.DOCUMENTOS.DOCUMENTO.EDITOR_TEXTO.REDIGIR,
					documento.id,
				)
				this.redirecionarPara(editor.urlEdicao)
			}
		},
		async baixarDocumentoAnexo(documentoAnexo) {
			await this.$store.dispatch(actionTypes.PEDIDO.DOCUMENTOS.BAIXAR_DOCUMENTO_ANEXO, documentoAnexo)
		},
		async excluirDocumentoAnexo() {
			await this.$store.dispatch(
				actionTypes.PEDIDO.DOCUMENTOS.EXCLUIR_DOCUMENTO_OU_ANEXO,
				this.documentoSelecionado,
			)

			this.corrigirPaginacaoQuandoContemApenasUmDocumento()
			await this.$emit('recarregar-listagem-externa')
			this.exibeMensagemSucessoExclusaoDocumento()
			this.abrirOuFecharModalExclusaoDocumentoAnexo(null)
		},
		async concluirEdicaoDocumento() {
			await this.$store.dispatch(
				actionTypes.PEDIDO.DOCUMENTOS.DOCUMENTO.EDITOR_TEXTO.SINCRONIZAR,
				this.documentoSelecionado.id,
			)

			this.mostrarNotificacaoSucesso('O documento está disponível como rascunho no processo digital')
			this.abrirOuFecharModalConcluirEdicaoDocumento(null)
			await this.$emit('recarregar-listagem-externa')
		},
		onSelectAll(value) {
			if (value && !this.isSelecionouTodosItensDaListaDeDocumentosPedido) {
				this.selecionarTodosItensListagem()
			} else {
				this.$store.commit(mutationTypes.PEDIDO.DOCUMENTOS.CHECKBOX.DESFAZER_SELECAO_ITENS_LISTAGEM)
			}
		},
		abrirOuFecharModalExclusaoDocumentoAnexo(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			this.documentoSelecionado = documento
			this.exibirModalExclusaoDocumentoAnexo = !this.exibirModalExclusaoDocumentoAnexo
		},
		resetarPage() {
			this.$emit('resetar-page')
		},
		podeExibirAcaoRedigir(item) {
			return (
				this.isDocumento(item) &&
				item.jaAssinou === false &&
				this.documentoEstaCriado(item) &&
				!this.isAtaCancelada
			)
		},
		estaJuntado(docOuAnexo) {
			if (this.documentoOuAnexoEstaJuntado(docOuAnexo)) return 'Sim'
			if (this.documentoOuAnexoEstaJuntadoDesatualizado(docOuAnexo)) return 'Sim'
			return 'Não'
		},
		estaDesatualizado(docOuAnexo) {
			return this.documentoOuAnexoEstaDesatualizado(docOuAnexo)
		},
		exibeMensagemSucessoExclusaoDocumento() {
			this.mostrarNotificacaoSucesso(
				`${this.getTextTipoDocumentoSelecionado()} do pedido foi excluído com sucesso!`,
			)
		},
		getTextTipoDocumentoSelecionado() {
			if (this.documentoSelecionado) {
				return this.tipoDocumentoRegistro[this.documentoSelecionado.tipoRegistroDocumento].text
			}

			return ''
		},
		getTituloDocumento(documento) {
			return `${documento.titulo}${
				this.isAnexo(documento.tipoRegistroDocumento)
					? ' ' + '(' + this.getConstantText(documento.extensao, this.extensoesArquivos, 'text') + ')'
					: ''
			}`
		},
		getTituloModalConfirmacaoExclusao() {
			return `Excluir ${this.getTextTipoDocumentoSelecionado()}`
		},
		getMensagemModalConfirmacaoExclusao() {
			return `O ${this.getTextTipoDocumentoSelecionado().toLowerCase()} será excluído e não poderá ser recuperado!`
		},
		corrigirPaginacaoQuandoContemApenasUmDocumento() {
			if (this.documentos.length === 1 && this.paginacaoInterna.page > 1) {
				this.paginacaoInterna.page -= 1
			}
		},
		selecionarTodosItensListagem() {
			this.$store.commit(mutationTypes.PEDIDO.DOCUMENTOS.CHECKBOX.SET_IS_SELECIONOU_TODOS_ITENS)
			this.documentosSelecionados = this.documentos.filter((item) => this.itemPodeSelecionar(item))
		},
		estaCancelado(docOuAnexo) {
			return this.documentoOuAnexoEstaCancelado(docOuAnexo)
		},

		abrirModalInformarMotivoCancelamento(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			this.documentoSelecionado = documento
			this.exibirModalInformarMotivoCancelamento = !this.exibirModalInformarMotivoCancelamento
		},

		async cancelarDocumento(payload) {
			this.exibirModalInformarMotivoCancelamento = !this.exibirModalInformarMotivoCancelamento

			await this.$store.dispatch(actionTypes.PEDIDO.DOCUMENTOS.PROCESSO_DIGITAL.CANCELAR_DOCUMENTO, payload)

			this.mostrarNotificacaoSucesso('Alteração realizada com sucesso')

			await this.$emit('recarregar-listagem-externa')
		},
		abrirOuFecharModalJuntarDocumento(documento) {
			this.documentoSelecionado = documento
			this.mensagemModalJuntarDocumento = documento.titulo
			this.exibirModalJuntarDocumento = !this.exibirModalJuntarDocumento
		},
		getMensagemModalConfirmacaoJuntado() {
			return `Atenção! O ${this.getTextTipoDocumentoSelecionado().toLowerCase()} a seguir será juntado ao processo digital:`
		},
		getCorpoMensagemModalConfirmacaoJuntado() {
			if (this.documentoSelecionado) {
				const tipoDocumento = this.tipoDocumentoRegistro[this.documentoSelecionado.tipoRegistroDocumento]

				if (this.parametrosSistema.isProcessoDigitalAtivo) {
					return `Ao juntar o ${tipoDocumento.text.toLowerCase()}, o número do processo não poderá ser alterado. Deseja continuar?`
				} else {
					return `Deseja realmente juntar?`
				}
			}
			return ''
		},
		cancelarAcaoJuntarDocumentoProcessoDigital() {
			this.documentoSelecionado = null
			this.exibirModalJuntarDocumento = false
		},
		async juntarDocumentoProcessoDigital() {
			const payload = {
				id: this.idPedido,
				idsDocumentos: [this.documentoSelecionado.id],
			}

			if (this.documentoSelecionado.ehTermoDesentranhamento === opcaoSimNao.SIM.value) {
				await this.$store.dispatch(
					actionTypes.PEDIDO.DOCUMENTOS.PROCESSO_DIGITAL.DESENTRANHAMENTO.JUNTAR_TERMO,
					this.documentoSelecionado.id,
				)
			} else {
				await this.$store.dispatch(
					actionTypes.PEDIDO.DOCUMENTOS.PROCESSO_DIGITAL.JUNTAR_LOTE_DOCUMENTOS,
					payload,
				)
			}
			this.documentoSelecionado.situacao = situacoes.DOCUMENTO.JUNTADO.value
			this.documentoSelecionado = null
			this.documentosSelecionados = []
			this.cancelarAcaoJuntarDocumentoProcessoDigital()
			this.mostrarNotificacaoSucesso('Documento foi juntado ao processo digital.')
			await this.$emit('recarregar-listagem-externa')
		},
		itemPodeSelecionar(item) {
			return (
				this.temProcessoDigital(item) &&
				!this.documentoOuAnexoEstaJuntado(item) &&
				!this.documentoOuAnexoEstaCancelado(item) &&
				!this.possuiTermoDesentranhamento(item)
			)
		},
		abrirOuFecharModalConcluirEdicaoDocumento(documento) {
			this.documentoSelecionado = documento
			if (this.parametrosSistema.isProcessoDigitalAtivo) {
				this.exibirModalConcluirEdicaoDocumento = !this.exibirModalConcluirEdicaoDocumento
			} else {
				this.concluirEdicaoDocumento()
			}
		},
		getMensagemModalConfirmacaoConcluirEdicaoDocumento() {
			return `Atenção!`
		},
		getCorpoMensagemModalConcluirEdicaoDocumento() {
			return `Ao concluir a edição do documento, o número do processo não poderá ser alterado. Deseja continuar?`
		},
		exibirIconeSituacaoEDocs(item) {
			return this.isIntegracaoEdocsAtivo && item.situacaoEdocs
		},
		isDocumentoEnviadoEdocs(documento) {
			return (
				documento &&
				(situacoesEdocs.AGUARDANDO.value === documento.situacaoEdocs ||
					situacoesEdocs.SINCRONIZADO.value === documento.situacaoEdocs)
			)
		},
	},
}
</script>
<style lang="stylus" scoped>
.documento-desatualizado
	color var(--v-grey-darken1)
	text-decoration line-through
</style>
